import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Input,
  Button,
  Select,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  IconButton,
  useToast,
  InputGroup,
  InputLeftElement
} from '@chakra-ui/react';
import { SearchIcon, AddIcon, EditIcon, DeleteIcon } from '@chakra-ui/icons';
import { supabase } from '../../supabaseClient';
import labTestOfferingsService from '../../utils/labTestOfferingsService';

const PAGE_SIZE = 50;

const TestOfferingsManagement = () => {
  const [tests, setTests] = useState([]);
  const [labs, setLabs] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [labOfferings, setLabOfferings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const debounceRef = useRef(null);
  const toast = useToast();

  const { isOpen: isAddModalOpen, onOpen: onAddModalOpen, onClose: onAddModalClose } = useDisclosure();
  const { isOpen: isEditModalOpen, onOpen: onEditModalOpen, onClose: onEditModalClose } = useDisclosure();

  const [editingOffering, setEditingOffering] = useState(null);
  const [offeringForm, setOfferingForm] = useState({
    testId: '',
    labId: '',
    price: '',
    code: '',
    notes: ''
  });

  useEffect(() => {
    fetchTests();
    fetchLabs();
  }, []);

  // Debounce search input
  const handleSearchChange = useCallback((e) => {
    const val = e.target.value;
    setSearchTerm(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(val);
      setCurrentPage(1);
    }, 300);
  }, []);

  // Paginated fetch to get ALL rows (Supabase caps at 1000 per request)
  const fetchAllRows = async (table, selectQuery) => {
    const PAGE = 1000;
    let allData = [];
    let from = 0;
    while (true) {
      const { data, error } = await supabase
        .from(table)
        .select(selectQuery)
        .order('name')
        .range(from, from + PAGE - 1);
      if (error) throw error;
      if (!data || data.length === 0) break;
      allData = allData.concat(data);
      if (data.length < PAGE) break;
      from += PAGE;
    }
    return allData;
  };

  const fetchTests = async () => {
    try {
      const selectCols = 'id, name, code, category, alternative_name, cash_price';
      const [testsData, panelsData] = await Promise.all([
        fetchAllRows('tests', selectCols),
        fetchAllRows('panels', selectCols),
      ]);

      const allTests = [
        ...testsData.map(t => ({ ...t, type: 'test' })),
        ...panelsData.map(p => ({ ...p, type: 'panel' })),
      ].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

      setTests(allTests);
    } catch (error) {
      toast({
        title: 'Error fetching tests',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const fetchLabs = async () => {
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLabs(data || []);
    } catch (error) {
      toast({
        title: 'Error fetching labs',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTestSelect = async (testId) => {
    setSelectedTest(testId);
    setLoading(true);
    try {
      const offerings = await labTestOfferingsService.getLabsForTest(testId);
      setLabOfferings(offerings);
    } catch (error) {
      toast({
        title: 'Error loading lab offerings',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddOffering = () => {
    setEditingOffering(null);
    setOfferingForm({
      testId: selectedTest || '',
      labId: '',
      price: '',
      code: '',
      notes: ''
    });
    onAddModalOpen();
  };

  const handleEditOffering = (offering) => {
    setEditingOffering(offering);
    setOfferingForm({
      testId: offering.test_id,
      labId: offering.lab_id,
      price: offering.lab_cash_price,
      code: offering.lab_test_code || '',
      notes: offering.lab_notes || ''
    });
    onEditModalOpen();
  };

  const handleSaveOffering = async () => {
    try {
      if (!offeringForm.testId || !offeringForm.labId || !offeringForm.price) {
        toast({
          title: 'Missing required fields',
          description: 'Please fill in all required fields',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (editingOffering) {
        await labTestOfferingsService.updateTestOffering(
          offeringForm.labId,
          offeringForm.testId,
          {
            price: parseFloat(offeringForm.price),
            code: offeringForm.code,
            notes: offeringForm.notes
          }
        );
        toast({
          title: 'Offering updated',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        await labTestOfferingsService.addTestOffering(
          offeringForm.labId,
          offeringForm.testId,
          {
            price: parseFloat(offeringForm.price),
            code: offeringForm.code,
            notes: offeringForm.notes
          }
        );
        toast({
          title: 'Offering added',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }

      if (selectedTest) {
        await handleTestSelect(selectedTest);
      }

      onAddModalClose();
      onEditModalClose();
    } catch (error) {
      toast({
        title: 'Error saving offering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteOffering = async (labId, testId) => {
    if (!window.confirm('Are you sure you want to remove this offering?')) {
      return;
    }

    try {
      await labTestOfferingsService.removeTestOffering(labId, testId);
      toast({
        title: 'Offering removed',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      if (selectedTest) {
        await handleTestSelect(selectedTest);
      }
    } catch (error) {
      toast({
        title: 'Error removing offering',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  // Acronym match: "BMP" matches "Basic Metabolic Panel"
  const matchesAcronym = useCallback((name, search) => {
    if (!name || !search) return false;
    const words = name.split(/\s+/).filter(w => w.length > 0);
    if (words.length < 2) return false;
    const acronym = words.map(w => w[0]).join('').toLowerCase();
    return acronym === search || acronym.startsWith(search);
  }, []);

  // Memoized filtered + paginated data
  const filteredTests = useMemo(() => {
    if (!debouncedSearch) return tests;
    const q = debouncedSearch.toLowerCase();
    return tests.filter(test =>
      test.name?.toLowerCase().includes(q) ||
      test.alternative_name?.toLowerCase().includes(q) ||
      matchesAcronym(test.name, q)
    );
  }, [tests, debouncedSearch, matchesAcronym]);

  const totalPages = Math.ceil(filteredTests.length / PAGE_SIZE);
  const paginatedTests = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredTests.slice(start, start + PAGE_SIZE);
  }, [filteredTests, currentPage]);

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Box>
          <Heading size="lg" mb={2}>Test Offerings Management</Heading>
          <Text color="gray.600">
            Manage which labs offer which tests and their pricing
          </Text>
        </Box>

        {/* Search and Actions */}
        <Card>
          <CardBody>
            <VStack spacing={4}>
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <SearchIcon color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder="Search tests by name or acronym (e.g. BMP, CBC)..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
              </InputGroup>

              {selectedTest && (
                <Button
                  leftIcon={<AddIcon />}
                  colorScheme="blue"
                  onClick={handleAddOffering}
                  width="full"
                >
                  Add Lab Offering for Selected Test
                </Button>
              )}
            </VStack>
          </CardBody>
        </Card>

        {/* Tests List */}
        <Card>
          <CardHeader>
            <HStack justify="space-between" flexWrap="wrap">
              <Heading size="md">Available Tests</Heading>
              <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>
                {filteredTests.length} test{filteredTests.length !== 1 ? 's' : ''}
                {debouncedSearch && ` (filtered)`}
              </Badge>
            </HStack>
          </CardHeader>
          <CardBody>
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th>Test Name</Th>
                  <Th>Code</Th>
                  <Th>Category</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {paginatedTests.map((test) => (
                  <Tr
                    key={test.id}
                    bg={selectedTest === test.id ? 'blue.50' : 'white'}
                    _hover={{ bg: 'gray.50' }}
                    cursor="pointer"
                    onClick={() => handleTestSelect(test.id)}
                  >
                    <Td fontWeight="semibold" maxW="400px" isTruncated title={test.name}>{test.name}</Td>
                    <Td fontFamily="mono">{test.code || 'N/A'}</Td>
                    <Td maxW="200px" isTruncated title={test.category || 'General'}>
                      <Badge colorScheme="blue">{test.category || 'General'}</Badge>
                    </Td>
                    <Td>
                      <Text fontSize="xs" color="blue.600">
                        Click to view labs →
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>

            {/* Pagination */}
            {totalPages > 1 && (
              <HStack justify="center" mt={4} spacing={4}>
                <Button
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  isDisabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Text fontSize="sm">
                  Page {currentPage} of {totalPages}
                </Text>
                <Button
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  isDisabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </HStack>
            )}
          </CardBody>
        </Card>

        {/* Lab Offerings */}
        {selectedTest && (
          <Card>
            <CardHeader>
              <HStack justify="space-between">
                <Heading size="md">
                  Labs Offering: {tests.find(t => t.id === selectedTest)?.name}
                </Heading>
                <Badge colorScheme="green" fontSize="md">
                  {labOfferings.length} lab{labOfferings.length !== 1 ? 's' : ''}
                </Badge>
              </HStack>
            </CardHeader>
            <CardBody>
              {loading ? (
                <Box textAlign="center" py={8}>
                  <Spinner size="xl" />
                  <Text mt={2}>Loading lab offerings...</Text>
                </Box>
              ) : labOfferings.length > 0 ? (
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Lab Name</Th>
                      <Th>Location</Th>
                      <Th>Price</Th>
                      <Th>Lab Code</Th>
                      <Th>Notes</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {labOfferings.map((offering) => (
                      <Tr key={`${offering.lab_id}-${offering.test_id}`}>
                        <Td fontWeight="semibold">{offering.labs.name}</Td>
                        <Td fontSize="sm" color="gray.600">
                          {offering.labs.address}
                        </Td>
                        <Td fontWeight="semibold" color="green.600">
                          {formatPrice(offering.lab_cash_price)}
                        </Td>
                        <Td fontFamily="mono" fontSize="sm">
                          {offering.lab_test_code || 'N/A'}
                        </Td>
                        <Td fontSize="xs" color="gray.500">
                          {offering.lab_notes || '-'}
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              colorScheme="blue"
                              variant="ghost"
                              onClick={() => handleEditOffering(offering)}
                            />
                            <IconButton
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteOffering(offering.lab_id, offering.test_id)}
                            />
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              ) : (
                <Alert status="info">
                  <AlertIcon />
                  <Box>
                    <AlertTitle>No lab offerings yet</AlertTitle>
                    <AlertDescription>
                      No labs currently offer this test. Click "Add Lab Offering" to add one.
                    </AlertDescription>
                  </Box>
                </Alert>
              )}
            </CardBody>
          </Card>
        )}

        {/* Instructions */}
        {!selectedTest && (
          <Alert status="info">
            <AlertIcon />
            <Box>
              <AlertTitle>How to use</AlertTitle>
              <AlertDescription>
                Click on a test from the list above to see which labs offer it and their pricing.
                You can add, edit, or remove lab offerings for any test.
              </AlertDescription>
            </Box>
          </Alert>
        )}
      </VStack>

      {/* Add/Edit Modal */}
      <Modal isOpen={isAddModalOpen || isEditModalOpen} onClose={editingOffering ? onEditModalClose : onAddModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingOffering ? 'Edit Lab Offering' : 'Add Lab Offering'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Lab</FormLabel>
                <Select
                  value={offeringForm.labId}
                  onChange={(e) => setOfferingForm({ ...offeringForm, labId: e.target.value })}
                  isDisabled={!!editingOffering}
                >
                  <option value="">Select a lab</option>
                  {labs.map(lab => (
                    <option key={lab.id} value={lab.id}>
                      {lab.name}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Price</FormLabel>
                <NumberInput
                  value={offeringForm.price}
                  onChange={(value) => setOfferingForm({ ...offeringForm, price: value })}
                  precision={2}
                  min={0}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Lab Test Code</FormLabel>
                <Input
                  value={offeringForm.code}
                  onChange={(e) => setOfferingForm({ ...offeringForm, code: e.target.value })}
                  placeholder="e.g., CBC001"
                />
              </FormControl>

              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Input
                  value={offeringForm.notes}
                  onChange={(e) => setOfferingForm({ ...offeringForm, notes: e.target.value })}
                  placeholder="Optional notes about this offering"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={editingOffering ? onEditModalClose : onAddModalClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSaveOffering}>
              {editingOffering ? 'Update' : 'Add'} Offering
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TestOfferingsManagement;
